import { find, get, groupBy } from 'lodash/fp';
import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { record } from 'fp-ts';
import {
    AsyncMetaState,
    createAsyncMetaSelectors,
    createReducer,
    createScopedReducer,
    createScopedSelector,
    LocalizedPropertyObject,
    Selector,
} from '@mews/framework';
import { getProp, getStringProp, keyBy } from '@mews/fp';
import { Identifiable } from '@mews/std';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { META_STATE_STARTED, META_STATE_SUCCEEDED, META_STATE_FAILED } from '../constants';

export interface MetaState {
    meta: AsyncMetaState;
}

interface EntityStateHandlerActions {
    FETCH_SUCCESS: string;
    ADD_SUCCESS: string;
    UPDATE_SUCCESS: string;
    DELETE_SUCCESS: string;
    RESET_SUCCESS: string;
}

export type Entities<Entity> = Record<string, Entity>;
export type ScopedEntities<Entity> = Record<string, Entities<Entity>>;
type GetEntities<State, Entity, Props> = Props extends object
    ? (state: State, props: Props) => Entities<Entity>
    : (state: State) => Entities<Entity>;

type GetEntitiesByIds<State, Entity, GetEntitiesProps> = (
    state: State,
    props: { ids: string[] } & GetEntitiesProps
) => Entity[];
type GetEntitiesByParameter<State, Entity, GetEntitiesProps, ParameterName extends string, ParameterValue = string> = (
    state: State,
    props: { [key in ParameterName]: ParameterValue } & GetEntitiesProps
) => Entity[];

export interface FetchSuccessEntitiesPayload<T> {
    entities?: Array<T> | null;
    ownerId?: string;
}

const normalizeEntities = <T>(entities: T[] | Record<string, T>) =>
    Array.isArray(entities) ? entities : Object.values(entities);

const INITIAL_STATE = {};

const createEntityStateHandler = ({
    FETCH_SUCCESS,
    ADD_SUCCESS,
    UPDATE_SUCCESS,
    DELETE_SUCCESS,
    RESET_SUCCESS,
}: EntityStateHandlerActions) => ({
    ...(FETCH_SUCCESS && {
        [FETCH_SUCCESS]: <Entity extends Identifiable>(
            state: Entities<Entity>,
            { entities, ownerId }: FetchSuccessEntitiesPayload<Entity>
        ) => {
            const normalizedEntities = entities ? normalizeEntities(entities) : [];
            return normalizedEntities.length > 0
                ? {
                      ...state,
                      ...keyBy(
                          'id',
                          ownerId ? normalizedEntities.map((entity) => ({ ...entity, ownerId })) : normalizedEntities
                      ),
                  }
                : state;
        },
    }),

    ...(ADD_SUCCESS && { [ADD_SUCCESS]: createEntityHandler(updateSingle, updateBulk) }),

    ...(UPDATE_SUCCESS && { [UPDATE_SUCCESS]: createEntityHandler(updateSingle, updateBulk) }),

    ...(DELETE_SUCCESS && { [DELETE_SUCCESS]: createEntityHandler(deleteSingle, deleteBulk) }),

    ...(RESET_SUCCESS && { [RESET_SUCCESS]: () => INITIAL_STATE }),
});

export const createEntitiesReducer = (actionTypes: $TSFixMe) =>
    createReducer(INITIAL_STATE, createEntityStateHandler(actionTypes));

// @ts-expect-error $TSFixMeError: Parameter 'actionTypes' implicitly has an 'any' type.
export const createScopedEntitiesReducer = (actionTypes) =>
    createScopedReducer(INITIAL_STATE, createEntityStateHandler(actionTypes));

export const createEntityHandler =
    (singleHandler: $TSFixMe, bulkHandler: $TSFixMe) => (state: $TSFixMe, props: $TSFixMe) =>
        Array.isArray(props) ? bulkHandler(state, props) : singleHandler(state, props);

// @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
const updateSingle = (state, { id, ...other }: $TSFixMe) => ({
    ...state,
    [id]: { ...state[id], id, ...other },
});

const updateBulk = <Entity extends Identifiable>(state: Entities<Entity>, entities: Entity[]) =>
    entities && entities.length > 0
        ? {
              ...state,
              ...keyBy('id', entities),
          }
        : state;

const deleteSingle = (state: $TSFixMe, { id }: $TSFixMe) => {
    const { [id]: omittedEntity, ...other } = state;
    return { ...other };
};

const deleteBulk = <Entity extends Identifiable>(state: Entities<Entity>, ids: string[]) =>
    ids && ids.length > 0
        ? pipe(
              state,
              record.filterWithIndex((id, _) => !ids.includes(id))
          )
        : state;

export const mapSearchResults = (searchType: $TSFixMe) => flow(find({ searchType }), get('result'));

type EntityConstrain<ParamName extends string, ParamValue> = { [key in ParamName]?: ParamValue };

type GetParam<Entity, ParamName extends string> = Entity extends EntityConstrain<ParamName, infer T> ? T : never;
export const createEntitySelectorBy =
    <ParameterName extends string>(matchingProp: ParameterName) =>
    <
        State,
        Entity extends EntityConstrain<ParameterName, unknown>,
        ParameterValue extends GetParam<Entity, ParameterName>,
        GetEntitiesProps,
    >(
        entitySelector: GetEntities<State, Entity, GetEntitiesProps>
    ): GetEntitiesByParameter<State, Entity, GetEntitiesProps, ParameterName, ParameterValue> =>
        createSelector(entitySelector, getProp<ParameterValue>()(matchingProp), (entities, propValue) =>
            Object.values(entities).filter((entity) => entity[matchingProp] === propValue)
        );

export const createEntityPropertyIncludesSelector = <
    State,
    Entity,
    GetEntitiesProps,
    PropertyName extends string,
    PropName extends string,
>(
    entitySelector: GetEntities<State, Entity, GetEntitiesProps>,
    propertyName: PropertyName,
    propName: PropName
) =>
    createSelector(
        createSelector(entitySelector, groupBy<Entity>(propertyName)),
        getProp<string[]>()(propName),
        (groupedEntities, searchValues) => {
            const pickedEntities: Entity[] = [];
            searchValues.forEach((id) => {
                const targetEntities = groupedEntities[id];
                if (targetEntities) {
                    pickedEntities.push(...targetEntities);
                }
            });
            return pickedEntities;
        }
    );

export const createEntitySelectorByCompanyId = createEntitySelectorBy('companyId');
export const createEntitySelectorByServiceId = createEntitySelectorBy('serviceId');
export const createEntitySelectorByAccountId = createEntitySelectorBy('accountId');
export const createEntitySelectorByCustomerId = createEntitySelectorBy('customerId');
export const createEntitySelectorByAvailabilityBlockId = createEntitySelectorBy('availabilityBlockId');
export const createEntitySelectorByResourceCategoryId = createEntitySelectorBy('resourceCategoryId');
export const createEntitySelectorByOwnerId = createEntitySelectorBy('ownerId');
export const createEntitySelectorByAssociatedAccountId = createEntitySelectorBy('associatedAccountId');
export const createEntitySelectorByGroupId = createEntitySelectorBy('groupId');

const createEntitySelectorByIds = <State, Entity, GetEntitiesProps>(
    entitySelector: GetEntities<State, Entity, GetEntitiesProps>
): GetEntitiesByIds<State, Entity, GetEntitiesProps> =>
    createSelector(getProp<string[]>()('ids'), entitySelector, (ids, entities) => {
        // This approach was selected as best performant one, creating a single array without any further objects.
        // Other approaches such as map + array or array.filterMap have additional overhead.
        const pickedEntities: Entity[] = [];
        ids.forEach((id) => {
            const pickedEntity = entities[id];
            if (pickedEntity) {
                pickedEntities.push(pickedEntity);
            }
        });
        return pickedEntities;
    });

type EntityCheck<Entity, ParamName extends string, ParamValue> = Entity extends EntityConstrain<ParamName, ParamValue>
    ? Entity
    : never;
const createEntitySelectors = <State, Entity, GetEntitiesProps>(
    getEntities: GetEntities<State, Entity, GetEntitiesProps>
) => {
    const getEntitiesByOwnerId = createEntitySelectorByOwnerId(
        getEntities as GetEntities<State, Entity & EntityCheck<Entity, 'ownerId', unknown>, GetEntitiesProps>
    );

    const getEntitiesByIds = createEntitySelectorByIds(getEntities);

    const getEntityById = createSelector(
        getEntities,
        getStringProp('id'),
        (entities, id): Entity | undefined => entities[id]
    );

    return [getEntities, getEntitiesByIds, getEntitiesByOwnerId, getEntityById] as const;
};

export const createBaseGetEntitiesSelector = <State, Entity, ModuleName extends string>(
    parentSelector: (state: State) => { [key in ModuleName]: Entities<Entity> },
    moduleName: ModuleName
): GetEntities<State, Entity, undefined> => createSelector(parentSelector, (state) => state[moduleName]);

export const createBaseEntitySelectors = <State, Entity, ModuleName extends string>(
    parentSelector: (state: State) => { [key in ModuleName]: Entities<Entity> },
    moduleName: ModuleName
) => {
    const getEntities = createBaseGetEntitiesSelector<State, Entity, ModuleName>(parentSelector, moduleName);

    return createEntitySelectors(getEntities);
};

export const createScopedBaseEntitySelectors = <State, Entity, ModuleName extends string, ScopeName extends string>(
    parentSelector: (state: State) => { [key in ModuleName]: ScopedEntities<Entity> },
    moduleName: ModuleName,
    scopeName: ScopeName
) => {
    const moduleSelector = createSelector(parentSelector, (state) => state[moduleName]);
    // @ts-expect-error $TSFixMeError: Type '(state: $TSFixMe, props: $TSFixMe) => any' is not assignable to type 'GetEntities<State, Entity, { [key in ScopeNa...
    const getEntities: GetEntities<State, Entity, { [key in ScopeName]: string }> = createScopedSelector({
        parentSelector: moduleSelector,
        scopeName,
    });
    return createEntitySelectors(getEntities);
};

export const createEntityAsyncMetaHandlers = ({ STARTED, SUCCEEDED, FAILED }: $TSFixMe) => ({
    [STARTED]: (state: $TSFixMe) => ({
        ...state,
        ...META_STATE_STARTED,
    }),
    [SUCCEEDED]: (state: $TSFixMe) => ({
        ...state,
        ...META_STATE_SUCCEEDED,
    }),
    [FAILED]: (state: $TSFixMe) => ({
        ...state,
        ...META_STATE_FAILED,
    }),
});

interface AsyncActionTypes {
    STARTED: string;
    SUCCEEDED: string;
    FAILED: string;
}

const createEntityAsyncMetaHandlers2 = ({ STARTED, SUCCEEDED, FAILED }: AsyncActionTypes) => ({
    [STARTED]: (state: AsyncMetaState) => ({ ...state, ...META_STATE_STARTED.meta }),
    [SUCCEEDED]: (state: AsyncMetaState) => ({ ...state, ...META_STATE_SUCCEEDED.meta }),
    [FAILED]: (state: AsyncMetaState) => ({ ...state, ...META_STATE_FAILED.meta }),
});

export const createAsyncMetaStateSelectors = <S, P>(metaSelector: Selector<S, P, MetaState>) => {
    const getMetaState = flow(metaSelector, (metaState) => metaState.meta);
    return createAsyncMetaSelectors(getMetaState);
};

export const createEntityMetaReducer = (asyncActionTypes: AsyncActionTypes) =>
    createReducer(META_STATE_SUCCEEDED.meta, createEntityAsyncMetaHandlers2(asyncActionTypes));

export const dispatchScopedGroupedEntityActions =
    ({ entities, action, scopeName }: $TSFixMe) =>
    (dispatch: $TSFixMe) => {
        const groupedEntities = groupBy(scopeName, entities);
        Object.entries(groupedEntities).forEach(([scope, groupedEntity]) => {
            dispatch(action(groupedEntity, scope));
        });
    };

interface EntityMeta {
    createdUtc: string;
    creatorProfileId: string;
    updatedUtc: string;
    updaterProfileId: string;
}
export const pickEntityMeta = ({ createdUtc, creatorProfileId, updatedUtc, updaterProfileId }: EntityMeta) => ({
    createdUtc,
    creatorProfileId,
    updatedUtc,
    updaterProfileId,
});

type PolymorphicEntitySelector<State, Entity, Props> = Props extends object
    ? (state: State, props: Props) => Entities<Entity> | Array<Entity>
    : (state: State) => Entities<Entity> | Array<Entity>;
export const localizedEntities = <
    Property extends string,
    TargetProperty extends string,
    State,
    Entity extends EntityConstrain<Property, LocalizedPropertyObject>,
    Props,
>(
    selector: PolymorphicEntitySelector<State, Entity, Props>,
    originalProperty: Property,
    targetProperty: TargetProperty
) =>
    createSelector(selector, getContainer, (entities, container) => {
        const { appLocalizationComponent } = container();
        const normalizedEntities = normalizeEntities(entities);
        return normalizedEntities.map((entity) => {
            const localizedProperty = appLocalizationComponent.localizeProperty(entity[originalProperty]);
            return { ...entity, [targetProperty]: localizedProperty };
        });
    });
